import { useState } from "react";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Briefcase,
  CalendarCheck,
  FileText,
  Pencil,
  Star,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ui/states";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { usePageNavigation } from "@/hooks/use-page-navigation";
import { useAgenciaResumo } from "@/hooks/useAgenciaResumo";
import { AgenciaEditModal } from "@/components/agencias/AgenciaEditModal";
import { AgenciaTimeline } from "@/components/agencias/timeline/AgenciaTimeline";
import { AgenciaFreelancersTab } from "@/components/agencias/AgenciaFreelancersTab";
import { AgenciaVagasTab } from "@/components/agencias/AgenciaVagasTab";
import { AgenciaAvaliacoesTab } from "@/components/agencias/AgenciaAvaliacoesTab";
import { PropostasTab } from "@/components/finance/PropostasTab";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { formatDateOnly } from "@/utils/dateUtils";
import type { Agencia } from "@/types/agencia";

function CardResumoNumero({
  titulo,
  valor,
  legenda,
}: {
  titulo: string;
  valor: number;
  legenda?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{titulo}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tabular-nums">{valor}</p>
        {legenda && <p className="text-xs text-muted-foreground">{legenda}</p>}
      </CardContent>
    </Card>
  );
}

export default function AgenciaDetails() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { navigateBack } = usePageNavigation({ pageStorageKey: "agencias_page" });
  const [editando, setEditando] = useState(false);
  const { screens } = useUserPermissions();
  const temAcessoFinanceiro = screens.includes("financeiro");

  const agenciaQuery = useQuery({
    queryKey: ["agencia", id],
    enabled: !!id,
    queryFn: async (): Promise<Agencia | null> => {
      const { data, error } = await supabase
        .from("agencias")
        .select()
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data as Agencia) ?? null;
    },
  });

  const resumoQuery = useAgenciaResumo(id);

  const handleBack = () => navigateBack("/agencias");

  if (agenciaQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (agenciaQuery.isError || !agenciaQuery.data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" className="gap-2" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <ErrorState
          title="Agência não encontrada"
          description="Não foi possível carregar os dados dessa agência."
          onRetry={() => agenciaQuery.refetch()}
        />
      </div>
    );
  }

  const agencia = agenciaQuery.data;
  const resumo = resumoQuery.data;

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={handleBack}>
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Button>

      {/* Header: nome, logo, categorias, favorita */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {agencia.logo_path && (
            <img
              src={agencia.logo_path}
              alt={`Logo de ${agencia.nome}`}
              className="h-14 w-14 shrink-0 rounded-lg border object-contain bg-white"
            />
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{agencia.nome}</h1>
              {agencia.favorita && (
                <Star
                  className="h-5 w-5 fill-yellow-400 text-yellow-400"
                  aria-label="Agência favorita"
                />
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {agencia.categorias?.map((categoria) => (
                <Badge key={categoria} variant="outline" className="font-bold">
                  Porte {categoria}
                </Badge>
              ))}
              {agencia.segmento && (
                <span className="text-sm text-muted-foreground">{agencia.segmento}</span>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => setEditando(true)}>
          <Pencil className="h-4 w-4" />
          Editar
        </Button>
      </div>

      {(agencia.sigla || agencia.razao_social || agencia.cnpj || agencia.endereco || agencia.contato_nome) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Dados para proposta
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {agencia.sigla && (
              <div>
                <span className="text-muted-foreground">Sigla: </span>
                {agencia.sigla}
              </div>
            )}
            {agencia.razao_social && (
              <div>
                <span className="text-muted-foreground">Razão social: </span>
                {agencia.razao_social}
              </div>
            )}
            {agencia.cnpj && (
              <div>
                <span className="text-muted-foreground">CNPJ: </span>
                {agencia.cnpj}
              </div>
            )}
            {agencia.endereco && (
              <div className="sm:col-span-2 lg:col-span-3">
                <span className="text-muted-foreground">Endereço: </span>
                {agencia.endereco}
              </div>
            )}
            {agencia.contato_nome && (
              <div>
                <span className="text-muted-foreground">Contato: </span>
                {agencia.contato_nome}
                {agencia.contato_cargo && ` (${agencia.contato_cargo})`}
              </div>
            )}
            {agencia.contato_email && (
              <div>
                <span className="text-muted-foreground">Email do contato: </span>
                {agencia.contato_email}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cards do golden record (v_agencia_resumo) */}
      {resumoQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : (
        resumo && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <CardResumoNumero
              titulo="Vagas abertas"
              valor={resumo.vagas_abertas}
              legenda={
                resumo.ultima_vaga_criada_em
                  ? `Última criada em ${formatDateOnly(resumo.ultima_vaga_criada_em)}`
                  : undefined
              }
            />
            <CardResumoNumero
              titulo="Vagas aprovadas"
              valor={resumo.vagas_aprovadas}
              legenda={
                resumo.ultima_aprovacao_em
                  ? `Última em ${formatDateOnly(resumo.ultima_aprovacao_em)}`
                  : undefined
              }
            />
            <CardResumoNumero
              titulo="Freelancers em projeto agora"
              valor={resumo.freelancers_alocados_agora}
              legenda={`${resumo.freelancers_historico_total} já trabalharam com a agência`}
            />
            <CardResumoNumero
              titulo="Projetos ativos agora"
              valor={resumo.projetos_ativos_agora}
            />
          </div>
        )
      )}

      {/* Tabs lazy: cada uma monta e busca seus dados só quando aberta */}
      <Tabs defaultValue="linha-do-tempo" className="w-full">
        <TabsList>
          <TabsTrigger value="linha-do-tempo">Linha do tempo</TabsTrigger>
          <TabsTrigger value="freelancers" className="gap-1.5">
            <Users className="h-3.5 w-3.5" />
            Freelancers
          </TabsTrigger>
          <TabsTrigger value="vagas" className="gap-1.5">
            <Briefcase className="h-3.5 w-3.5" />
            Vagas
          </TabsTrigger>
          <TabsTrigger value="avaliacoes" className="gap-1.5">
            <CalendarCheck className="h-3.5 w-3.5" />
            Avaliações
          </TabsTrigger>
          {temAcessoFinanceiro && (
            <TabsTrigger value="propostas" className="gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Propostas
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="linha-do-tempo" className="mt-6">
          {id && <AgenciaTimeline agenciaId={id} />}
        </TabsContent>
        <TabsContent value="freelancers" className="mt-6">
          {id && <AgenciaFreelancersTab agenciaId={id} />}
        </TabsContent>
        <TabsContent value="vagas" className="mt-6">
          {id && <AgenciaVagasTab agenciaId={id} />}
        </TabsContent>
        <TabsContent value="avaliacoes" className="mt-6">
          {id && <AgenciaAvaliacoesTab agenciaId={id} />}
        </TabsContent>
        {temAcessoFinanceiro && (
          <TabsContent value="propostas" className="mt-6">
            {id && <PropostasTab fixedAgenciaId={id} />}
          </TabsContent>
        )}
      </Tabs>

      {editando && (
        <AgenciaEditModal
          agencia={agencia}
          onClose={() => setEditando(false)}
          onSuccess={() => {
            setEditando(false);
            queryClient.invalidateQueries({ queryKey: ["agencia", id] });
            queryClient.invalidateQueries({ queryKey: ["agencias"] });
          }}
        />
      )}
    </div>
  );
}
